import { isSkinUnlocked, type SkinId } from "./skins";

const STORAGE_KEY = "cyborg-tomato-save-v1";
const SAVE_VERSION = 1;

export interface Settings {
  skin: SkinId;
  muted: boolean;
  reducedMotion: boolean;
  highContrast: boolean;
  toggleControl: boolean;
  invertHold: boolean;
}

export interface SaveData {
  version: number;
  bestScore: number;
  bestDepth: number;
  completedRandom: boolean;
  unlockedStamps: string[];
  settings: Settings;
}

const DEFAULT_SETTINGS: Settings = {
  skin: "heirloom",
  muted: false,
  reducedMotion: false,
  highContrast: false,
  toggleControl: false,
  invertHold: false,
};

function defaults(): SaveData {
  return {
    version: SAVE_VERSION,
    bestScore: 0,
    bestDepth: 0,
    completedRandom: false,
    unlockedStamps: [],
    settings: { ...DEFAULT_SETTINGS },
  };
}

export function loadSave(): SaveData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults();
    const parsed = JSON.parse(raw) as Partial<SaveData> & { completedDaily?: unknown };
    if (parsed.version !== SAVE_VERSION) return defaults();
    const unlockedStamps = Array.isArray(parsed.unlockedStamps)
      ? parsed.unlockedStamps
          .filter((value): value is string => typeof value === "string" && value !== "5 PATENTS VOIDED" && value !== "20 PATENTS VOIDED")
          .map((value) => value === "DAILY TIMELINE" ? "RANDOM TIMELINE" : value)
      : [];
    const bestScore = finiteNonNegative(parsed.bestScore);
    const storedSkin = parsed.settings?.skin;
    return {
      version: SAVE_VERSION,
      bestScore,
      bestDepth: finiteNonNegative(parsed.bestDepth),
      completedRandom: parsed.completedRandom === true || parsed.completedDaily === true,
      unlockedStamps: [...new Set(unlockedStamps)],
      settings: {
        skin: isSkinUnlocked(storedSkin, bestScore) ? storedSkin : "heirloom",
        muted: parsed.settings?.muted === true,
        reducedMotion: parsed.settings?.reducedMotion === true,
        highContrast: parsed.settings?.highContrast === true,
        toggleControl: parsed.settings?.toggleControl === true,
        invertHold: parsed.settings?.invertHold === true,
      },
    };
  } catch {
    return defaults();
  }
}

export function save(data: SaveData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Storage may be unavailable in private browsing. The active run continues.
  }
}

function updateBest(data: SaveData, score: number, depth: number): boolean {
  const previous = data.bestScore;
  data.bestScore = Math.max(data.bestScore, Math.round(finiteNonNegative(score)));
  data.bestDepth = Math.max(data.bestDepth, Math.round(finiteNonNegative(depth)));
  return data.bestScore > previous;
}

export function recordBest(data: SaveData, score: number, depth: number): boolean {
  const improved = updateBest(data, score, depth);
  save(data);
  return improved;
}

export function recordRun(data: SaveData, score: number, depth: number, random: boolean): string[] {
  updateBest(data, score, depth);
  if (random) data.completedRandom = true;

  const earned: Array<[string, boolean]> = [
    ["25D DESCENT", data.bestDepth >= 1_000],
    ["50D DESCENT", data.bestDepth >= 2_000],
    ["100D DESCENT", data.bestDepth >= 4_000],
    ["RANDOM TIMELINE", data.completedRandom],
  ];
  const newlyUnlocked: string[] = [];
  for (const [label, qualified] of earned) {
    if (qualified && !data.unlockedStamps.includes(label)) {
      data.unlockedStamps.push(label);
      newlyUnlocked.push(label);
    }
  }
  save(data);
  return newlyUnlocked;
}

function finiteNonNegative(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : 0;
}
